import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiLogin, apiLogout, apiGetMe, type CurrentUser } from '@/services/auth';
import { getToken } from '@/services/http';

const USER_KEY = 'gtra_current_user';

export function saveCurrentUser(user: CurrentUser): void {
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function loadCurrentUser(): CurrentUser | null {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    if (raw) return JSON.parse(raw) as CurrentUser;
    return null;
  } catch {
    return null;
  }
}

export function clearCurrentUser(): void {
  sessionStorage.removeItem(USER_KEY);
}

export interface AdminAuthState {
  isAuthenticated: boolean;
  loading: boolean;
  currentUser: CurrentUser | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

export function useAdminAuth(): AdminAuthState {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(loadCurrentUser);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // On mount: if we have a token but no cached user, fetch from API
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    const cached = loadCurrentUser();
    if (cached) {
      setCurrentUser(cached);
      setLoading(false);
      return;
    }
    // Validate token with /me
    apiGetMe()
      .then((user) => {
        saveCurrentUser(user);
        setCurrentUser(user);
      })
      .catch(() => {
        // Token invalid
        clearCurrentUser();
        setCurrentUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    console.log('🔐 尝试登录:', email);
    try {
      const { user } = await apiLogin({ email, password });
      console.log('✅ 登录成功，用户信息:', user);
      saveCurrentUser(user);
      setCurrentUser(user);
      console.log('✅ 用户信息已保存到 sessionStorage');
      return true;
    } catch (error) {
      console.error('❌ 登录失败:', error);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      clearCurrentUser();
      setCurrentUser(null);
      navigate('/admin/login');
    }
  }, [navigate]);

  return {
    isAuthenticated: !!currentUser && !!getToken(),
    loading,
    currentUser,
    login,
    logout,
  };
}

/** Synchronous check — for route guards (reads stored token) */
export function checkAdminAuth(): boolean {
  return !!getToken() && !!loadCurrentUser();
}
